import { tailwindMergeConfig } from "@repo/ui/lib/utils";
import { type ClassValue, clsx } from "clsx";
import { format } from "date-fns";
import { extendTailwindMerge } from "tailwind-merge";

const twMerge = extendTailwindMerge(tailwindMergeConfig);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

/**
 * Safely formats a date, returning empty string if date is null/undefined/invalid.
 * Uses date-fns format under the hood.
 */
export function safeFormatDate(
  date: Date | string | null | undefined,
  formatStr: string = "MMM d, yyyy",
): string {
  if (!date) {
    return "";
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return "";
  }

  return format(dateObj, formatStr);
}

/**
 * Trim a label to `maxLength` characters and mark the cut with an ellipsis.
 * The ellipsis is added on top of the budget, so the result can be three
 * characters longer than `maxLength`. An absent label renders as empty.
 */
export function truncateText(
  text: string | null | undefined,
  maxLength: number = 30,
): string {
  if (!text) {
    return "";
  }
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + "...";
}

/**
 * Whether `text` would render wider than `maxWidth` at the navigation's font,
 * measured on a detached canvas. Returns false during SSR, where there is no
 * document to measure against and the caller should render the label untrimmed.
 */
export function shouldTruncate(text: string, maxWidth: number = 300): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return false;
  }

  context.font =
    '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  return context.measureText(text).width > maxWidth;
}

/** Fisher-Yates shuffle returning a new array. */
export function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}
